<template>
  <div
    class="layout"
    ref="scrollLayoutContainer"
  >
    <header
      ref="headerRef"
      class="header-pc"
    >
      <HeaderTopSource v-if="!showCeiling" />
      <HeaderTop v-if="route.path === '/' && !showCeiling" /> <!-- 应该是searchTop -->
      <div
        class="search-ceiling"
        :class="{ ceiling: showCeiling, 'ceiling-up': scrollDirection === 'up', 'ceiling-down': scrollDirection === 'down' }"
      >
        <Search :guidance-pop-visible="guidancePopVisible" /> <!-- input 搜索框区域 购物车 订单 个人中心等-->
        <CategorySlider />
      </div>
      <Header v-if="!showCeiling" /> <!-- shop of confidence -->
    </header>
    <div
      :style="{ height: headerHeight + 'px' }"
      v-if="showCeiling"
    />
    <section
      class="content"
      :style="contentStyle"
    >
      <RouterView />
    </section>
    <Footer />
    <NewbieGuidance
      :pop-visible="guidancePopVisible"
      :title="currentGuidance.title"
      :video-src="currentGuidance.videoSrc"
      @close="closeGuidancePop"
    />
    <Toast
      :msg="toastInfo.msg"
      :type="toastInfo.type"
      :visible="toastVisible"
    />
    <LoginModal position="center" />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, onBeforeUnmount, nextTick, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

import Header from '@/components/Header/index.vue'
import HeaderTop from '@/components/HeaderTop/index.vue'
import HeaderTopSource from '@/components/HeaderTopSource/index.vue'
import Search from '@/components/Search/index.vue'
import CategorySlider from '@/components/CategorySlider/index.vue'
import Footer from '@/components/Footer/index.vue'
import NewbieGuidance from '@/components/NewbieGuidance/index.vue'
import Toast from '@/components/Toast/index.vue'
import { LoginModal } from '@/components/Login'

import { fetchData } from '@/utils/fetch'
import { buriedPointUtil, gaAnalytics, debounce } from '@/utils/utils'
import { useUserStore } from '@/pinia/user'

const store = useStore()
const route = useRoute()

const router = useRouter()
const user = useUserStore()

const scrollLayoutContainer = ref(null)
const isLoading = ref(false)
const hasMore = ref(true)
const showCeiling = ref(false)
const headerRef = ref(null) // 创建 ref 引用
const headerHeight = ref(0)

const lastScrollY = ref(0)
const scrollDirection = ref('')

const guidancePopVisible = computed(
  () => store.state.common.guidancePopVisible
)
const currentGuidance = computed(() => store.state.common.currentGuidance)
const toastVisible = computed(() => store.state.common.toastVisible)
const toastInfo = computed(() => store.state.common.toastInfo)

const searchInfo = computed(() => store.state.searchInfo)
const productList = computed(() => store.state.productList)

const emptyObj = computed(() => store.state.emptyObj)

const contentStyle = computed(() => {
  return {
    minHeight: window.innerHeight - headerHeight.value - 63.5 + 'px'
  }
})
const initSkeleton = computed(() => store.state.initSkeleton)

const closeGuidancePop = () => {
  store.commit('common/SET_GUIDANCE_POP_VISIBLE', false)
  // store.commit("common/SET_ANIMATE_HELPCENTER", true);
}

const getHashParam = (key) => {
  const hash = window.location.hash.substring(1)
  const params = new URLSearchParams(hash)
  return params.get(key)
}

// 滚动加载
const layoutOnScroll = () => {
  const pageScrollY = window.scrollY
  const scrollHeight = document.documentElement.scrollHeight
  const clientHeight = window.innerHeight

  if (pageScrollY > headerHeight.value) {
    if (pageScrollY > lastScrollY.value) {
      scrollDirection.value = 'down' // 向下滚动
      store.commit('RECORD_CEILING_VISIBLE', false)
    } else {
      // 临界值判断 (距离顶部 20px 时触发隐藏动画)
      if (pageScrollY < headerHeight.value + 20) {
        scrollDirection.value = '' // 清除方向
        showCeiling.value = false // 隐藏 ceiling
      } else {
        scrollDirection.value = 'up' // 向上滚动
        showCeiling.value = true // 显示 ceiling
      }
      store.commit('RECORD_CEILING_VISIBLE', showCeiling.value)
    }
  } else {
    showCeiling.value = false // 隐藏 ceiling
    scrollDirection.value = ''
    store.commit('RECORD_CEILING_VISIBLE', showCeiling.value)
  }
  lastScrollY.value = pageScrollY

  // 判断当前位置加 75px 是否大于等于最大滚动位置
  if (route.path === '/search') {
    if (pageScrollY + 75 >= scrollHeight - clientHeight && scrollHeight > clientHeight) {
      loadMore()
    }
  }
}

// 图搜
const searchByImage = async () => {
  // if (productList.value.length < searchInfo.value.pageSize) {
  //   hasMore = false;
  // }
  const searchData = {
    ...searchInfo.value,
    pageNow: searchInfo.value.pageNow + 1
  }
  buriedPointUtil({ eventId: 'PSC401', others: { ...searchData } })

  const { code, data } = await fetchData('/dsp-app/alibaba-common/searchImage', {
    method: 'post',
    body: JSON.stringify(searchData)
  })
  if (code === 200) {
    if (!data.rows || data.rows?.length === 0) {
      hasMore.value = false
    }
    const rows = [...productList.value, ...data.rows]
    store.dispatch('setImgSearch', {
      rows,
      total: data.total,
      searchInfo: searchData
    })
  }
}

// ai discovery 加载更多
const loadMoreAiDiscovery = async () => {
  if (!productList.value.length) {
    hasMore.value = false
  }
  const searchData = {
    ...searchInfo.value,
    pageNow: searchInfo.value.pageNow + 1,
    language: 'zh',
    async: false
  }
  if (keyWordCn.value) {
    searchData.keyword = keyWordCn.value
  }
  buriedPointUtil({ eventId: 'PSC401', others: { ...searchData } })
  const { code, data } = await fetchData('/dsp-app/alibaba-common/searchKeyWord?itemNumber=50', {
    method: 'post',
    body: JSON.stringify(searchData)
  })
  if (code === 200) {
    if (!data.rows || !data.rows.length) {
      hasMore.value = false
    }

    const rows = [...productList.value, ...data.rows]
    const currencyData = JSON.parse(localStorage.getItem('currencyData'))
    const list = rows.map((v) => {
      return {
        ...v,
        priceInfo: { price: v.priceInfo.price, ...currencyData }
      }
    })
    store.commit('setSearchInfo', searchData)
    store.commit('setProductList', list)
    store.commit('setTotal', data.total)
  }
}

// 加载更多
const loadMore = debounce(async () => {
  if (isLoading.value || !hasMore.value) return
  /**
   * 文搜(关键字或者类目搜索)功能内部自己管理
   * TODO: 等后续做 图搜 相关功能后，移除全局 loadMore 事件，由组件内部自己处理
   */
  if (route.query.keyword || route.query.categoryId) return

  if (emptyObj?.code === 5209) {
    return
  }
  gaAnalytics('p_search_page_load_more', 'p_search_page_load_more')

  try {
    isLoading.value = true
    store.commit('setLoading', true)
    store.commit('setLoadMoreSleleton', true)

    // similar 图搜
    if (route.query.type === 'similar') {
      await searchByImage()
    }
    // ai discovery (原 styles 搜索)
    if (route.query.type === 'discovery') {
      if (initSkeleton.value) {
        isLoading.value = false
        return
      }
      await loadMoreAiDiscovery()
    }

    isLoading.value = false
    store.commit('setLoading', false)
    store.commit('setLoadMoreSleleton', false)
    store.commit('search/SET_PRE_SEARCH', false)
  } catch (error) {
    isLoading.value = false
    store.commit('setLoading', false)
    store.commit('setLoadMoreSleleton', false)
    store.commit('search/SET_PRE_SEARCH', false)
  }
}, 300)

// 等待元素出现
const waitForElement = () => {
  return new Promise((resolve) => {
    const observer = new MutationObserver((mutationsList, observer) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const element = document.querySelector('.intercom-launcher')
          if (element) {
            observer.disconnect() // 找到元素后停止观察
            resolve(element) // 返回找到的元素
          }
        }
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
  })
}

onMounted(async () => {
  // 监听 windows 滚动
  window.addEventListener('scroll', layoutOnScroll)
  gaAnalytics('p_page_view', 'p_page_view', {
    bdUserId: user.userInfo.uid
  })
  buriedPointUtil({ eventId: 'PWH501' })

  waitForElement().then((element) => {
    element.addEventListener('click', function () {
      gaAnalytics('p_consultation_click', 'p_consultation_click')
      buriedPointUtil({ eventId: 'PWH202' })
    })
  })

  nextTick(() => {
    if (headerRef.value) {
      headerHeight.value = headerRef.value.offsetHeight // 获取高度
      console.log('headerHeight', headerHeight.value)
    }
  })
})

// 根据坐标裁剪图片

const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

const preSearch = computed(() => store.state.search.preSearch)
watch(preSearch, (value) => {
  if (value) {
    loadMore()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', layoutOnScroll)
})
</script>

<style scoped>
.layout {
  position: relative;
  overflow: hidden;

  .content {
    /* padding-bottom: 64px; */
  }

  .ceiling {
    position: fixed;
    z-index: 999;
    visibility: hidden;
    opacity: 0;
    /* transform: translateY(-100%); */
    transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
  }

  .ceiling-up {
    visibility: initial;
    opacity: 1;
    transform: translateY(0px);
  }

  .ceiling-down {
    visibility: initial;
    opacity: 0;
    transform: translateY(-100%);
  }
}
</style>
